import { Component, OnInit } from '@angular/core';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';

import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../shared/helpers/ordenacao-tabela.helper';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ListaUsuarioResponse } from '../../../shared/services/responses/usuario/lista-usuario.response';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { RouterModule } from '@angular/router';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UsuarioListaStruct } from '../../../shared/services/structs/usuario/usuario-lista.struct';
import { FoneMaskPipe } from '../../../shared/pipes/fone-mask.pipe';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { ViewChild } from '@angular/core';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-usuario-lista',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    RouterModule,
    EmptyListComponent,
    MatTooltipModule,
    FoneMaskPipe,
    MatMenuModule,
    MatSortModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './usuario-lista.component.html',
  styleUrl: './usuario-lista.component.css',
})
export class UsuarioListaComponent implements OnInit {
  public moduloEnum: ModuloEnum;
  public funcionalidadeEnum: FuncionalidadeEnum;
  public model: FormGroup;
  public _isLoading: boolean = false;
  public _listaUsuarioResponse: ListaUsuarioResponse | undefined;
  public displayedColumns: string[] = ['idUsuario', 'nomeUsuario', 'email', 'telefone', 'actions'];
  public _dataSource = new DataSourceOrdenacaoInsensivel<UsuarioListaStruct>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private usuarioService: UsuarioService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {
    this.moduloEnum = ModuloEnum.Configuracoes;
    this.funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Usuario;
  }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      nomeUsuario: [''],
    });

    this.loadFilters();
    this.pesquisar();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/usuario/cadastrar',
    ]);
  }

  private pesquisar(): void {
    if (this.model.invalid || this._isLoading == true) {
      return;
    }

    this._isLoading = true;
    const nomeUsuario: string = this.model.get('nomeUsuario')?.value;

    this.usuarioService.get(nomeUsuario, this._paginaAtual).subscribe({
      next: (usuarioLoginResponse: ListaUsuarioResponse) => {
        this._isLoading = false;
        if (usuarioLoginResponse.isError) {
          this.alertService.show('Erro', usuarioLoginResponse.errorDescription, AlertType.error);
          return;
        }

        this._ultimaPagina = true;

        if (!this._listaUsuarioResponse) {
          this._listaUsuarioResponse = usuarioLoginResponse;
          this._ultimaPagina = false;
        } else if (usuarioLoginResponse.listaUsuario?.length > 0) {
          this._listaUsuarioResponse.listaUsuario.push(...usuarioLoginResponse.listaUsuario);
          this._ultimaPagina = false;
        }

        this._dataSource.data = this._listaUsuarioResponse.listaUsuario;
      },
      error: error => {
        this.alertService.show('Erro inesperado', error, AlertType.error);
        this._isLoading = false;
      },
    });
  }

  proximaPagina(): void {
    this._paginaAtual = this._paginaAtual + 1;
    this.pesquisar();
  }

  clickPesquisar(): void {
    this._paginaAtual = 0;
    this._listaUsuarioResponse = undefined;
    this.pesquisar();
  }

  abrirModalExcluir(idUsuario: number, nomeUsuario: string): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Alerta',
        description: `Deseja excluir o usuário ${nomeUsuario}? Essa ação será irreversível.`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result && result.confirm) {
          this.excluir(idUsuario);
        }
      },
    });
  }

  private loadFilters() {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  private excluir(idUsuario: number): void {
    this.usuarioService.excluir(idUsuario).subscribe({
      next: (returnStruct: ReturnStruct) => {
        this._isLoading = false;
        if (returnStruct.isError) {
          this.alertService.show('Erro', returnStruct.errorDescription, AlertType.error);
          return;
        }

        this.clickPesquisar();

        this.alertService.show('Sucesso', 'Usuário excluido com sucesso', AlertType.success);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error, AlertType.error);
        this._isLoading = false;
      },
    });
  }
}
